import { useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { COUNTRIES } from "../../lib/translations";
import { Reveal } from "../Reveal";
import { BookingModal } from "./BookingModal";

const WHATSAPP_URL =
  process.env.REACT_APP_WHATSAPP_GROUP_URL ||
  "https://wa.me/message/F5WWGV3HOATTL1";
const CONTACT_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;

const INITIAL_FORM = {
  company: "",
  country: "",
  workers: "",
  industry: "",
  jobType: "",
  phone: "",
  name: "",
  email: "",
  contactMethod: "",
};

const LABEL_CLS =
  "block text-[10.5px] uppercase tracking-[0.18em] font-semibold mb-1.5";
const LABEL_STYLE = { color: "var(--fg-muted)" };
const TRIGGER_STYLE = {
  borderBottom: "1px solid var(--border)",
  borderRadius: 0,
  color: "var(--ink)",
  paddingTop: "10px",
  paddingBottom: "10px",
  height: "auto",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;

const validateForm = (form) => {
  const e = {};
  if (!form.company.trim()) e.company = true;
  if (!form.name.trim()) e.name = true;
  if (!EMAIL_RE.test(form.email.trim())) e.email = true;
  if (form.phone && !PHONE_RE.test(form.phone.trim())) e.phone = true;
  return e;
};

const postSubmission = async (payload) => {
  const res = await fetch(CONTACT_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  return { ok: res.ok, status: res.status };
};

const ThankYou = ({ t, thankYou, modalOpen, setModalOpen, onSendAnother }) => (
  <div
    data-testid="thank-you"
    className="p-8 md:p-12"
    style={{
      background: "var(--white)",
      borderRadius: "var(--radius-card)",
      boxShadow: "var(--shadow-md)",
      border: "1px solid var(--border)",
    }}
  >
    <h3
      className="font-serif text-2xl md:text-3xl"
      style={{ color: "var(--heading)" }}
    >
      {thankYou.method === "meeting"
        ? t.form.thankYou.meetingTitle
        : t.form.thankYou.title}
    </h3>
    <p
      className="mt-4 text-base leading-relaxed"
      style={{ color: "var(--fg-muted)" }}
    >
      {thankYou.method === "meeting"
        ? t.form.thankYou.meetingBody
        : t.form.thankYou.body}
    </p>

    {thankYou.method === "whatsapp" && (
      <div
        className="mt-8 p-5"
        style={{ background: "var(--paper)", borderRadius: "var(--radius-md)" }}
      >
        <p className="text-sm mb-4" style={{ color: "var(--fg-muted)" }}>
          {t.form.thankYou.whatsappNote}
        </p>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="thank-you-whatsapp"
          className="btn btn-gold"
        >
          {t.form.thankYou.whatsappCta}
          <ArrowUpRight size={16} />
        </a>
      </div>
    )}

    {thankYou.method === "meeting" && !modalOpen && (
      <button
        type="button"
        data-testid="reopen-booking"
        onClick={() => setModalOpen(true)}
        className="btn btn-gold mt-8"
      >
        {t.form.pickATime}
        <ArrowUpRight size={16} />
      </button>
    )}

    <button
      type="button"
      data-testid="send-another"
      onClick={onSendAnother}
      className="btn btn-ghost-light mt-6 ml-0 md:ml-3"
    >
      {t.form.thankYou.sendAnother}
    </button>
  </div>
);

const MethodChips = ({ methods, value, onChange }) => (
  <div
    role="radiogroup"
    data-testid="method-chips"
    className="flex flex-wrap gap-2.5 mt-2"
  >
    {methods.map((m) => {
      const active = value === m.key;
      return (
        <button
          key={m.key}
          type="button"
          role="radio"
          aria-checked={active}
          data-testid={`method-${m.key}`}
          data-active={active}
          onClick={() => onChange(active ? "" : m.key)}
          style={{
            borderRadius: "var(--radius-pill)",
            border: active
              ? "1px solid var(--gold-500)"
              : "1px solid var(--border)",
            background: active ? "var(--gold-500)" : "transparent",
            color: active ? "var(--green-800)" : "var(--ink)",
            padding: "9px 16px",
            fontSize: "12.5px",
            fontWeight: 600,
            letterSpacing: "0.04em",
            minHeight: 44,
            transition:
              "background var(--dur) var(--ease), color var(--dur) var(--ease), border-color var(--dur) var(--ease)",
          }}
        >
          {m.label}
        </button>
      );
    })}
  </div>
);

const SubmitButton = ({ submitting, t, lang }) => (
  <button
    type="submit"
    data-testid="submit-request"
    disabled={submitting}
    className="btn btn-gold"
  >
    {submitting ? (
      <>
        <span
          aria-hidden="true"
          style={{
            width: 14,
            height: 14,
            border: "2px solid rgba(255,255,255,0.4)",
            borderTopColor: "var(--white)",
            borderRadius: "50%",
            display: "inline-block",
            animation: "spin 0.7s linear infinite",
          }}
        />
        {lang === "uk" ? "Надсилаємо…" : "Sending…"}
      </>
    ) : (
      <>
        {t.form.submit}
        <ArrowUpRight size={16} />
      </>
    )}
  </button>
);

/**
 * Legacy custom-built request form. Kept for one-click rollback via the
 * SHOW_LEGACY_FORM toggle in `RequestForm.jsx`. When active, submits to
 * `/api/contact` which forwards to Zoho Flow.
 */
export const LegacyRequestForm = ({ t, lang }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [thankYou, setThankYou] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const update = (k) => (e) => {
    setForm((p) => ({ ...p, [k]: e.target ? e.target.value : e }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const errStyle = (k) =>
    errors[k] ? { borderBottomColor: "#c0392b" } : undefined;

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validateForm(form);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      toast.error(
        lang === "uk"
          ? "Перевірте обов'язкові поля та формат e-mail / телефону."
          : "Please check required fields and e-mail / phone format."
      );
      return;
    }
    setSubmitting(true);
    const payload = {
      source: "kadratalent.com",
      locale: lang,
      submittedAt: new Date().toISOString(),
      ...form,
    };
    try {
      const r = await postSubmission(payload);
      if (!r.ok) throw new Error("network");
      setThankYou({
        method: form.contactMethod,
        name: form.name,
        email: form.email,
        phone: form.phone,
      });
      if (form.contactMethod === "meeting") {
        setModalOpen(true);
      } else {
        toast.success(t.form.success);
      }
      setForm(INITIAL_FORM);
    } catch {
      toast.error(t.form.submitError || "Submit failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <BookingModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        prefill={thankYou}
        t={t}
        lang={lang}
      />
      <Reveal delay={180}>
        {thankYou ? (
          <ThankYou
            t={t}
            thankYou={thankYou}
            modalOpen={modalOpen}
            setModalOpen={setModalOpen}
            onSendAnother={() => {
              setThankYou(null);
              setModalOpen(false);
            }}
          />
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="request-form"
            className="p-6 md:p-10"
            style={{
              background: "var(--white)",
              borderRadius: "var(--radius-card)",
              boxShadow: "var(--shadow-md)",
              border: "1px solid var(--border)",
            }}
            noValidate
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.company} *
                </label>
                <input
                  data-testid="field-company"
                  type="text"
                  className="input-bare"
                  style={errStyle("company")}
                  value={form.company}
                  onChange={update("company")}
                  placeholder="—"
                />
              </div>
              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.country}
                </label>
                <Select
                  value={form.country}
                  onValueChange={(v) =>
                    setForm((p) => ({ ...p, country: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="field-country"
                    className="w-full bg-transparent border-0 px-0 focus:ring-0 hover:bg-transparent"
                    style={TRIGGER_STYLE}
                  >
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent
                    data-testid="country-options"
                    className="max-h-80"
                    style={{
                      background: "var(--white)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                      boxShadow: "var(--shadow-md)",
                    }}
                  >
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.en} value={c.en}>
                        {lang === "uk" ? c.uk : c.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.workers}
                </label>
                <input
                  data-testid="field-workers"
                  type="number"
                  min="1"
                  className="input-bare"
                  value={form.workers}
                  onChange={update("workers")}
                  placeholder="—"
                />
              </div>
              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.industry}
                </label>
                <Select
                  value={form.industry}
                  onValueChange={(v) =>
                    setForm((p) => ({ ...p, industry: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="field-industry"
                    className="w-full bg-transparent border-0 px-0 focus:ring-0 hover:bg-transparent"
                    style={TRIGGER_STYLE}
                  >
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent
                    data-testid="industry-options"
                    style={{
                      background: "var(--white)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-md)",
                      boxShadow: "var(--shadow-md)",
                    }}
                  >
                    {t.industries.items.map((i) => (
                      <SelectItem key={i.title} value={i.title}>
                        {i.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.jobType}
                </label>
                <input
                  data-testid="field-jobtype"
                  type="text"
                  className="input-bare"
                  value={form.jobType}
                  onChange={update("jobType")}
                  placeholder="—"
                />
              </div>
              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.phone}
                </label>
                <input
                  data-testid="field-phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  className="input-bare"
                  style={errStyle("phone")}
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+__ ___ ___ ___"
                />
              </div>

              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.name} *
                </label>
                <input
                  data-testid="field-name"
                  type="text"
                  autoComplete="name"
                  className="input-bare"
                  style={errStyle("name")}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="—"
                />
              </div>
              <div>
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.email} *
                </label>
                <input
                  data-testid="field-email"
                  type="email"
                  autoComplete="email"
                  className="input-bare"
                  style={errStyle("email")}
                  value={form.email}
                  onChange={update("email")}
                  placeholder="—"
                />
              </div>

              <div className="md:col-span-2">
                <label className={LABEL_CLS} style={LABEL_STYLE}>
                  {t.form.fields.contactMethod}
                </label>
                <MethodChips
                  methods={t.form.methods}
                  value={form.contactMethod}
                  onChange={(v) =>
                    setForm((p) => ({ ...p, contactMethod: v }))
                  }
                />
              </div>
            </div>

            <div
              className="mt-8 pt-6 flex flex-wrap items-center justify-between gap-4"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <span
                className="eyebrow"
                style={{ color: "var(--fg-muted)", fontSize: "10.5px" }}
              >
                {t.form.gdpr}
              </span>
              <SubmitButton submitting={submitting} t={t} lang={lang} />
            </div>
          </form>
        )}
      </Reveal>
    </>
  );
};
